import * as React from 'react';
// Import form hooks.
import {
	useFormInsuredInfo,
	UseFormInsuredInfoResults,
} from '@app/hooks/view';
// Import props.
import {
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
	ComparativeInsuredInformationSummaryProps,
	IconButtonProps,
} from '@app/props';
// Import enumerations.
import {
	EntityInstanceType,
} from '@app/enumerations';
// Import api models.
import {
	Address,
	AddressVerification,
	GpsCoordinatesWithAccuracy,
	InsuredInfo,
    ThirdPartyPropertyDataCommercial,
    ThirdPartyPropertyDataPersonal,
} from '@app/models/api';
// Import interfaces.
import {
	IAddress,
} from '@app/interfaces';
// Import services.
import {
	StringService,
} from '@app/services';
// Import mui modules.
import {
	EditOutlined,
	Person,
} from '@mui/icons-material';

interface UseComparativeInsuredInformationSummaryParameters {
	props: ComparativeInsuredInformationSummaryProps;
}

interface UseComparativeInsuredInformationSummaryResults {
	oldFormInsuredInfo: UseFormInsuredInfoResults;
	newFormInsuredInfo: UseFormInsuredInfoResults;
	insuredInformationMailingAddressBaseId: string;
	insuredInformationContactBaseId: string;
	applyInsuredInformationComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyInsuredInformationMailingAddressComparativeDataRowProps: () => ComparativeDataRowProps<IAddress>;
	applyInsuredInformationMailingAddressComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyInsuredInformationContactComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyInsuredInformationContactComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
}

const useComparativeInsuredInformationSummary = (params: UseComparativeInsuredInformationSummaryParameters): UseComparativeInsuredInformationSummaryResults => {

	// #region exported methods

	/**
	 * Returns insured information comparative data header props. 
	 **/
	const applyInsuredInformationComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${insuredInformationBaseId}-header`,
			isSticky: true,
			title: 'Insured Information',
			titleIcon: Person,
			titleFontSize: 'md',
			iconButtons: params.props.comparisonInsuredInfoSummary.allowEdits
				? getHeaderEditIconButton(params.props.onEditInsuredInformation, insuredInformationEditButtonRef)
				: undefined,
		};
	};

	/**
	 * Returns insured information mailing address comparative data row props. 
	 **/
	const applyInsuredInformationMailingAddressComparativeDataRowProps = (): ComparativeDataRowProps<IAddress> => {
		return {
			oldValue: oldFormInsuredInfo.addressVerification.addressLocationInput.value.address ?? undefined,
			newValue: newFormInsuredInfo.addressVerification.addressLocationInput.value.address ?? undefined,
			hideNewValueIfSame: true,
			hideDivider: true,
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns insured information address comparative data header props. 
	 **/
	const applyInsuredInformationMailingAddressComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${insuredInformationMailingAddressBaseId}-header`,
			isSticky: false,
			title: 'Mailing Address',
			titleFontSize: 'sm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns insured information contact comparative data row props. 
	 **/
	const applyInsuredInformationContactComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		const oldSubformInsuredInfoContactProps = oldFormInsuredInfo.applySubformInsuredInfoContactProps();
		const newSubformInsuredInfoContactProps = newFormInsuredInfo.applySubformInsuredInfoContactProps();

		let comparativeDataRowProps: ComparativeDataRowProps[] = [
			{
				fieldLabel: oldSubformInsuredInfoContactProps.insuredNameInput.label,
				oldValue: oldSubformInsuredInfoContactProps.insuredNameInput.value.toTitleCase(),
				newValue: newSubformInsuredInfoContactProps.insuredNameInput.value.toTitleCase(),
				hideNewValueIfSame: true,
			},
		];

		if (oldSubformInsuredInfoContactProps.isInsuredLastNameRequired) {
			comparativeDataRowProps.push({
				fieldLabel: oldSubformInsuredInfoContactProps.insuredLastInput.label,
				oldValue: oldSubformInsuredInfoContactProps.insuredLastInput.value.toTitleCase(),
				newValue: newSubformInsuredInfoContactProps.insuredLastInput.value.toTitleCase(),
				hideNewValueIfSame: true,
			});
		}

		comparativeDataRowProps.push(
			{
				fieldLabel: oldSubformInsuredInfoContactProps.insuredHomePhoneInput.label,
				oldValue: StringService.getInstance().formatPhoneNumber(oldSubformInsuredInfoContactProps.insuredHomePhoneInput.value),
				newValue: StringService.getInstance().formatPhoneNumber(newSubformInsuredInfoContactProps.insuredHomePhoneInput.value),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldSubformInsuredInfoContactProps.insuredMobilePhoneInput.label,
				oldValue: StringService.getInstance().formatPhoneNumber(oldSubformInsuredInfoContactProps.insuredMobilePhoneInput.value),
				newValue: StringService.getInstance().formatPhoneNumber(newSubformInsuredInfoContactProps.insuredMobilePhoneInput.value),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldSubformInsuredInfoContactProps.insuredEmailInput.label,
				oldValue: oldSubformInsuredInfoContactProps.insuredEmailInput.value.toTitleCase(),
				newValue: newSubformInsuredInfoContactProps.insuredEmailInput.value.toTitleCase(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldSubformInsuredInfoContactProps.sendPaymentReceiptToEmailInput.label,
				oldValue: oldSubformInsuredInfoContactProps.sendPaymentReceiptToEmailInput.getSelectedValueDisplayName()?.toTitleCase() ?? undefined,
				newValue: newSubformInsuredInfoContactProps.sendPaymentReceiptToEmailInput.getSelectedValueDisplayName()?.toTitleCase() ?? undefined,
				hideNewValueIfSame: true,
			},
		);
		return comparativeDataRowProps;
	};

	/**
	 * Returns insured information contact comparative data header props. 
	 **/
	const applyInsuredInformationContactComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${insuredInformationContactBaseId}-header`,
			isSticky: false,
			title: 'Contact Information',
			titleFontSize: 'sm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Generates id for the insured info form.
	 */
	const generateIdForFormInsuredInfo = (instanceType: EntityInstanceType): string => {
		return `${insuredInformationBaseId}-${instanceType}-form`;
	}

	/**
	 * Fetches the header edit icon button.
	 */
	const getHeaderEditIconButton = (onEdit: () => void, ref: React.RefObject<HTMLButtonElement>): IconButtonProps[] => {
		let iconButtons: IconButtonProps[] = [];
		if (params.props.comparisonInsuredInfoSummary.allowEdits) {
			iconButtons.push({
				label: 'Edit',
				onClick: () => onEdit(),
				icon: EditOutlined,
				ref: ref,
			});
		}

		return iconButtons;
	}

	// #endregion private methods


	// #region variables

	const focusToErrorOnMount: boolean = React.useMemo(() => params.props.focusToErrorOnMount ?? false, [params.props.focusToErrorOnMount]);
	const insuredInformationEditButtonRef: React.RefObject<HTMLButtonElement> = React.useRef<HTMLButtonElement>(null);

	const insuredInformationBaseId: string = React.useMemo(() => {
		return `${params.props.id}-insuredInformation`;
	}, [params.props.id]);

	const insuredInformationMailingAddressBaseId: string = React.useMemo(() => {
		return `${insuredInformationBaseId}-mailingAddress`;
	}, [insuredInformationBaseId]);

	const insuredInformationContactBaseId: string = React.useMemo(() => {
		return `${insuredInformationBaseId}-contact`;
	}, [insuredInformationBaseId]);

	// Old form insured info.
	const oldFormInsuredInfo: UseFormInsuredInfoResults = useFormInsuredInfo({
		id: generateIdForFormInsuredInfo(EntityInstanceType.OLD),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			possibleStates: params.props.possibleStates,
			insuredInfo: params.props.comparisonInsuredInfoSummary.oldData,
			onSaveInsuredInfo: (insuredInfo: InsuredInfo) => { },
			addressProps: {
				geocodeAddress: async (address: Address) => new GpsCoordinatesWithAccuracy(),
				getAddressSuggestions: async (addressQuery: string) => [],
				verifyAddress: async (address: Address) => new AddressVerification(),
				verifySingleLineAddress: async (singleLineAddress: string) => new AddressVerification(),
				getThirdPartyPropertyDataCommercial: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataCommercial(),
				getThirdPartyPropertyDataPersonal: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataPersonal(),
			},
			showValidationsOnMount: true,
		},
	});

	// New form insured info.
	const newFormInsuredInfo: UseFormInsuredInfoResults = useFormInsuredInfo({
		id: generateIdForFormInsuredInfo(EntityInstanceType.NEW),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			possibleStates: params.props.possibleStates,
			insuredInfo: params.props.comparisonInsuredInfoSummary.newData,
			onSaveInsuredInfo: (insuredInfo: InsuredInfo) => { },
			addressProps: {
				geocodeAddress: async (address: Address) => new GpsCoordinatesWithAccuracy(),
				getAddressSuggestions: async (addressQuery: string) => [],
				verifyAddress: async (address: Address) => new AddressVerification(),
				verifySingleLineAddress: async (singleLineAddress: string) => new AddressVerification(),
				getThirdPartyPropertyDataCommercial: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataCommercial(),
				getThirdPartyPropertyDataPersonal: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataPersonal(),
			},
			showValidationsOnMount: true,
		},
	});

	// #endregion private variables

	// #region lifecycle methods.

	React.useEffect(() => {
		if (focusToErrorOnMount
			&& params.props.comparisonInsuredInfoSummary.requiresReview
			&& insuredInformationEditButtonRef.current !== null) {
			insuredInformationEditButtonRef.current.focus();
		}
	}, []);

	// #endregion lifecycle methods.

	return {
		oldFormInsuredInfo,
		newFormInsuredInfo,
		insuredInformationMailingAddressBaseId,
		insuredInformationContactBaseId,
		applyInsuredInformationComparativeDataHeaderProps,
		applyInsuredInformationMailingAddressComparativeDataRowProps,
		applyInsuredInformationMailingAddressComparativeDataHeaderProps,
		applyInsuredInformationContactComparativeDataRowProps,
		applyInsuredInformationContactComparativeDataHeaderProps,
	};
}

// Export parameters.
export {
	UseComparativeInsuredInformationSummaryParameters,
}

// Export results.
export {
	UseComparativeInsuredInformationSummaryResults,
}

export default useComparativeInsuredInformationSummary;